<template>
  <!--
    Two-column layout: image left, form right. The right (form) column is intentionally
    wider so headlines and the phone input have room without wrapping awkwardly.
  -->
  <div class="grid overflow-hidden md:grid-cols-[2fr_3fr]">
    <!-- LEFT panel: full-bleed image; the gray background stretches full height when there is no image -->
    <div class="relative min-h-[16rem] overflow-hidden bg-stone-100">
      <img v-if="imageUrl" :src="imageUrl" alt="" class="absolute inset-0 h-full w-full object-cover" />

      <div
        v-if="storeTagline || storeName"
        class="absolute inset-x-0 bottom-0 p-6 text-center"
        :class="imageUrl ? 'bg-gradient-to-t from-black/50 to-transparent' : ''"
      >
        <p v-if="storeTagline" class="text-2xl italic" :class="imageUrl ? 'text-white' : 'text-stone-600'">
          {{ storeTagline }}
        </p>
        <p
          v-if="storeName"
          class="mt-1 text-xs font-semibold uppercase tracking-widest"
          :class="imageUrl ? 'text-white/90' : 'text-stone-500'"
        >
          {{ storeName }}
        </p>
      </div>
    </div>

    <!-- RIGHT panel: form or success -->
    <div class="flex flex-col justify-between bg-white px-6 py-8">
      <!-- Success state -->
      <div v-if="submitted" class="flex flex-col items-center justify-center text-center">
        <div v-if="reward" class="w-full">
          <p class="text-lg font-bold text-gray-900">You're in!</p>
          <p class="mt-1 text-sm text-gray-500">Here's your reward code:</p>
          <div class="mt-3 flex items-center justify-center gap-2 rounded-md bg-gray-100 px-4 py-3">
            <span class="font-mono text-base font-bold tracking-widest text-gray-900">{{ reward.code }}</span>
            <button
              class="ml-2 rounded px-2 py-1 text-xs font-semibold text-brand-600 hover:bg-gray-200 focus:outline-none"
              @click="copyCode"
            >
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
          <p v-if="reward.description" class="mt-2 text-sm text-gray-500">{{ reward.description }}</p>
        </div>
        <div v-else>
          <p class="text-lg font-bold text-gray-900">Thanks!</p>
          <p class="mt-1 text-sm text-gray-500">You're all set.</p>
        </div>
        <div class="mt-6 w-full">
          <button class="py-1 text-sm text-gray-500 underline hover:text-gray-700" @click="$emit('dismiss')">
            Close
          </button>
        </div>
      </div>

      <!-- Form state -->
      <template v-else>
        <div class="flex flex-col">
          <!-- Audience badge -->
          <div class="mb-4">
            <span
              class="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800"
            >
              <span class="mr-1.5 h-2 w-2 rounded-full bg-green-500"></span>
              {{ audienceLabel }}
            </span>
          </div>

          <!-- Headline -->
          <h2 v-if="headline" class="text-3xl font-bold leading-tight text-gray-900">
            {{ headline }}
          </h2>

          <!-- Body text -->
          <p v-if="text" class="mt-3 leading-relaxed text-gray-500">{{ text }}</p>

          <!-- Email input -->
          <div v-if="collectFromCustomer === 'email_address'" class="mt-5">
            <input
              v-model="emailValue"
              type="email"
              placeholder="Enter your email"
              class="w-full rounded-md border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <!-- Phone input -->
          <div v-else-if="collectFromCustomer === 'contact_number'" class="mt-5">
            <input
              id="popup-phone"
              ref="phoneInput"
              type="tel"
              placeholder="Enter your phone number"
              class="form-input w-full rounded-md border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <!-- Inline error -->
          <p v-if="error" class="mt-3 text-sm font-medium text-red-600">{{ error }}</p>
        </div>

        <!-- Actions + consent -->
        <div class="mt-6 flex flex-col items-center gap-3">
          <PrimaryButton :size="'lg'" class="w-full" @click="handleSubmit">Join our reminder list</PrimaryButton>

          <p v-if="collectFromCustomer === 'contact_number'" class="text-center text-xs text-gray-400">
            By joining you agree to receive recurring marketing texts from {{ storeName || 'us' }} at the number above.
            Consent isn't a condition of purchase. Msg &amp; data rates may apply. Reply STOP to opt out.
          </p>
          <p v-else-if="collectFromCustomer === 'email_address'" class="text-center text-xs text-gray-400">
            By joining you agree to receive marketing emails from {{ storeName || 'us' }}. You can unsubscribe at any
            time.
          </p>

          <button class="text-sm text-gray-500 underline hover:text-gray-700" @click="$emit('dismiss')">
            No thanks
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/build/css/intlTelInput.css'
import utilsJs from 'intl-tel-input/build/js/utils.js'
import PrimaryButton from '@/components/Molecules/PrimaryButton'

const AUDIENCE_LABELS = {
  all: 'Special Offer',
  first_time: 'First Time Customer Offer',
  returning: 'Returning Customer Offer'
}

export default {
  name: 'PopupCard',
  components: { PrimaryButton },
  props: {
    headline: {
      type: String,
      default: null
    },
    text: {
      type: String,
      default: null
    },
    imageUrl: {
      type: String,
      default: null
    },
    collectFromCustomer: {
      type: String,
      default: 'none'
    },
    audience: {
      type: String,
      default: null
    },
    submitted: {
      type: Boolean,
      default: false
    },
    reward: {
      type: Object,
      default: null
    },
    error: {
      type: String,
      default: ''
    },
    storeName: {
      type: String,
      default: ''
    },
    storeTagline: {
      type: String,
      default: ''
    }
  },
  emits: ['submit', 'dismiss'],
  data() {
    return {
      emailValue: '',
      intlTelInstance: null,
      copied: false
    }
  },
  computed: {
    audienceLabel() {
      return AUDIENCE_LABELS[this.audience] || 'Special Offer'
    }
  },
  mounted() {
    if (this.collectFromCustomer === 'contact_number' && this.$refs.phoneInput) {
      this.intlTelInstance = intlTelInput(this.$refs.phoneInput, {
        utilsScript: utilsJs
      })
    }
  },
  beforeUnmount() {
    if (this.intlTelInstance) {
      this.intlTelInstance.destroy()
    }
  },
  methods: {
    handleSubmit() {
      if (this.collectFromCustomer === 'email_address') {
        this.$emit('submit', { email: this.emailValue })
      } else if (this.collectFromCustomer === 'contact_number') {
        const countryCode = this.intlTelInstance?.getSelectedCountryData().dialCode || ''
        const fullNumber = this.intlTelInstance?.getNumber() || ''
        const phone = fullNumber.substring(countryCode.length + 1)
        this.$emit('submit', { phone, countryCode })
      } else {
        this.$emit('submit', {})
      }
    },
    copyCode() {
      if (!this.reward?.code) return
      navigator.clipboard.writeText(this.reward.code).then(() => {
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 2000)
      })
    }
  }
}
</script>
<style>
.iti {
  @apply w-full;
}
</style>
